import { is } from 'ramda';
import React, { MouseEventHandler, ReactElement, forwardRef } from 'react';
import styled from 'styled-components';

import { getColorFromTheme } from '@utils/theme';

import theme from '@theme/FolxTheme';

interface ICheckboxProps {
	name: string;
	value: string | boolean;
	children?: string | ReactElement;
	className?: string;
	disabled?: boolean;
	onClick?: MouseEventHandler;
	onLabelClick?: MouseEventHandler;
	optionLabel?: string;
}

interface ICheckboxWithRefProps {
	id: string;
	label: string;
	name: string;
	value: string | boolean;
	className?: string;
	disabled?: boolean;
	onClick?: MouseEventHandler;
}

type Ref = HTMLInputElement;

const Checkbox = forwardRef<Ref, ICheckboxWithRefProps>((args, ref) => (
	<input type="checkbox" {...args} aria-label={args.label} ref={ref} />
));

const CheckboxLabel = styled.label`
	align-items: center;

	background: ${theme.tints.lighten40(theme.colors.white)};
	border: 0.5px solid ${theme.colors.black};
	display: flex;
	font-size: ${theme.fontSizes.body.sm};
	justify-content: center;
	font-size: ${theme.fontSizes.body.sm};
	transition: background ease-in 0.2s;
	text-decoration: none;
	padding: calc(${theme.spacing.md} - 1px);
`;

const StyledCheckbox = styled(Checkbox)`
	&:checked + ${CheckboxLabel} {
		background: ${({ theme: components }) =>
			getColorFromTheme(components.selectableButton.color, components.selectableButton.tint)};
		border: 3px solid
			${({ theme: components }) =>
				getColorFromTheme(
					components.selectableButton.borderColor,
					components.selectableButton.borderTint
				)};
		padding: calc(${theme.spacing.md} - 3px);
	}
	&:focus + ${CheckboxLabel} {
		outline: 5px auto Highlight;
		outline: 5px auto -webkit-focus-ring-color;
	}
	&:focus:not(:focus-visible) + ${CheckboxLabel} {
		outline: none;
	}
	position: absolute;
	z-index: -1;
`;

const CheckboxButton = forwardRef<Ref, ICheckboxProps>(
	(
		{
			name,
			value,
			className,
			disabled = false,
			onClick,
			onLabelClick,
			children,
			optionLabel,
			...rest
		},
		ref
	) => {
		return (
			<>
				<StyledCheckbox
					{...rest}
					className={className}
					disabled={disabled}
					id={name}
					label={is(String, children) ? children : optionLabel}
					name={name}
					onClick={onClick}
					ref={ref}
					value={value}
				/>
				<CheckboxLabel htmlFor={name} className={className} onClick={onLabelClick}>
					{children}
				</CheckboxLabel>
			</>
		);
	}
);

export default styled(CheckboxButton)``;
CheckboxButton.displayName = 'CheckboxButton';
